<template>
    <!--
        La classe e `sc-table` e non un nome nuovo: `assets/scss/components/_table.scss` stila gia
        `.sc-table .el-table` e `.sc-table .pagination-bar`, ed era la classe che rendeva
        `vue-data-tables`. Cambiarla farebbe perdere bordo della tabella e distanza dal paginatore -
        che e proprio cio che si e visto al primo giro.
    -->
    <div class="sc-table">
        <ElTable
            ref="elTable"
            v-loading="loading"
            :data="data"
            v-bind="tableProps"
            style="width: 100%"
            @sort-change="alCambioOrdinamento"
            @selection-change="$emit('selection-change', $event)"
            @row-click="$emit('row-click', $event)"
            @current-change="$emit('current-change', $event)"
            @select="(selezione, riga) => $emit('select', selezione, riga)"
            @select-all="$emit('select-all', $event)"
        >
            <slot />

            <!-- La colonna delle azioni, dichiarata dai chiamanti come oggetto e non come markup. -->
            <ElTableColumn v-if="actionCol" :label="actionCol.label" v-bind="actionCol.props">
                <template #default="scope">
                    <!--
                        Lo `span` attorno a ogni bottone non e decorativo: la spaziatura fra le azioni
                        arriva da `.action-list > span + span`, ed e cosi che la rendeva
                        `vue-data-tables`. Senza involucro i bottoni restano attaccati.
                    -->
                    <div class="action-list">
                        <!--
                            Due aggiunte facoltative, per la lista Report: `visible(row)` nasconde
                            l'azione su certe righe (modifica e pubblicazione solo sui report non ancora
                            pubblicati) e `icon` permette bottoni con la sola icona. Senza di esse il
                            comportamento e quello di prima, quindi le altre liste non cambiano.
                        -->
                        <!--
                            `visibility: hidden` e non `v-show`: con `display: none` un bottone nascosto
                            **collassa**, e i rimanenti scorrono a sinistra, quindi la stessa azione
                            cambia posizione da una riga all'altra. Cosi invece ogni azione ha sempre la
                            sua colonna, e scorrendo l'elenco dall'alto in basso i bottoni dello stesso
                            tipo restano incolonnati anche dove qualcuno manca.
                        -->
                        <span
                            v-for="(bottone, i) in actionCol.buttons"
                            :key="i"
                            :class="{ 'action-list__nascosto': bottone.visible && !bottone.visible(scope.row) }"
                        >
                            <ElButton
                                v-bind="propsBottone(bottone)"
                                @click="bottone.handler(scope.row, scope.$index, scope.column)"
                            >
                                <font-awesome-icon v-if="bottone.icon" :icon="bottone.icon" />
                                <span v-if="bottone.label" :class="bottone.icon ? 'ml-2' : ''">
                                    {{ bottone.label }}
                                </span>
                            </ElButton>
                        </span>
                    </div>
                </template>
            </ElTableColumn>
        </ElTable>

        <div v-if="layout !== 'table'" class="pagination-bar">
            <!-- `pagination-wrap` e cio che centra il paginatore: la regola sta in `_table.scss`. -->
            <div class="pagination-wrap">
                <ElPagination
                    v-model:current-page="paginaCorrente"
                    v-model:page-size="dimensionePagina"
                    :total="total"
                    layout="total, sizes, prev, pager, next, jumper"
                    v-bind="paginationProps"
                    @size-change="alCambioDimensione"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';

type Ordinamento = { prop?: string; order?: string };
type Filtro = { value: unknown; prop?: string | string[]; [k: string]: unknown };
type TipoQuery = 'init' | 'sort' | 'filter' | 'page' | 'size';

/**
 * Tabella con paginazione lato server, al posto di `vue-data-tables`.
 *
 * **Perche riscritta e non aggiornata**: `vue-data-tables@3.4.5` non ha una versione Vue 3. Usa i
 * mixin e `render(h)` di Vue 2, e in `mounted` sostituisce l'`$emit` dell'`el-table` figlio per
 * rilanciarne gli eventi - un accorgimento che in Vue 3 non e riproducibile. Il contratto che i 37
 * punti d'uso sfruttano e pero piccolo, ed e quello che questo componente riproduce.
 *
 * **Il contratto verso il backend.** `query-change` emette l'oggetto che i chiamanti serializzano e
 * mandano al backend, dove `Pulinet.Shared/DataTable/QueryInfo.cs` lo deserializza. Due dettagli non
 * negoziabili, entrambi appresi dal comportamento della libreria precedente:
 *
 *  - `sort` parte come **oggetto vuoto**, non come `{ prop: null }`. Il backend fa `ToTitleCase()`
 *    su `sort.prop` e su `null` solleva `NullReferenceException`: inizializzarlo con le chiavi a
 *    `null` farebbe fallire con 500 la **prima** chiamata di ogni lista.
 *  - si emette un **oggetto nuovo a ogni chiamata**. I chiamanti gli aggiungono `andFilter = false`
 *    prima di serializzarlo: su un oggetto condiviso quella scrittura resterebbe appiccicata alle
 *    chiamate successive.
 *
 * **La ricerca dipende dal watch profondo.** `doSearch()` nei chiamanti non chiama nessuna fetch:
 * muta soltanto `filters[].value`. Senza `deep: true` la ricerca smette di funzionare **senza alcun
 * errore** - e il guasto piu silenzioso di questo cluster.
 *
 * **Una correzione inclusa.** Il totale mostrato e `total` cosi come arriva. La libreria precedente
 * calcolava `pageSize * currentPage` quando `total` era 0, e scriveva "Totale 20" sopra "Nessun
 * dato" (`llm/code-debt/architecture.md`, voce 0-bis). Da qui i 23 punti d'uso che passano
 * `class: total === 0 ? 'd-none' : ...` per nascondere il paginatore: quell'aggiramento ora e
 * superfluo, ma si lascia dov'e - toglierlo e un intervento a se.
 *
 * Comportamento riprodotto **cosi com'e**, anche dove e discutibile: cambiando i filtri la pagina
 * corrente **non** torna a 1. Cercare stando a pagina 3 chiede la pagina 3 del risultato filtrato.
 * Correggerlo dentro il porting mescolerebbe due cose diverse.
 */
export default defineComponent({
    name: 'PulinetDataTable',

    props: {
        data: { type: Array as PropType<unknown[]>, default: () => [] },
        total: { type: Number, default: 0 },
        pageSize: { type: Number, default: 20 },
        filters: { type: Array as PropType<Filtro[]>, default: () => [] },
        loading: { type: Boolean, default: false },
        paginationProps: { type: Object as PropType<Record<string, unknown>>, default: () => ({}) },
        tableProps: { type: Object as PropType<Record<string, unknown>>, default: () => ({}) },
        actionCol: { type: Object as PropType<any>, default: null },
        /**
         * `layout="table"` chiede la sola tabella, senza paginatore. Lo usava la variante lato client
         * della libreria precedente, e nel progetto compare in un punto solo.
         */
        layout: { type: String, default: 'tool,table,pagination' },
    },

    emits: ['query-change', 'selection-change', 'row-click', 'current-change', 'select', 'select-all'],

    data() {
        // `pageSize` va gia corretto qui, prima della prima emissione: se non e fra i valori offerti dal
        // paginatore, paginatore e query si disallineano. La libreria precedente faceva lo stesso, al
        // prezzo di un avviso in console.
        const offerte = (this.paginationProps?.pageSizes as number[] | undefined) ?? undefined;
        const dimensione = offerte?.length && !offerte.includes(this.pageSize) ? offerte[0] : this.pageSize;

        return {
            paginaCorrente: 1,
            dimensionePagina: dimensione,
            ordinamento: {} as Ordinamento,
        };
    },

    computed: {
        /**
         * Lo stato corrente della query. Esposto perche `ServicesTranslationTable.ts` lo legge dal `ref`
         * per rilanciare la fetch senza passare da un evento.
         */
        queryInfo(): Record<string, unknown> {
            return this.componiQuery('init');
        },
    },

    watch: {
        // La ricerca passa **solo** di qui: `doSearch()` muta `filters[].value` e nient'altro.
        filters: {
            handler() {
                this.emetti('filter');
            },
            deep: true,
        },

        paginaCorrente() {
            this.emetti('page');
        },

        pageSize(nuova: number) {
            this.dimensionePagina = nuova;
        },
    },

    created() {
        // Prima del mount, come faceva la libreria: la lista si popola senza attendere il ciclo di vita.
        this.emetti('init');
    },

    methods: {
        /**
         * Le prop del singolo bottone azione, con il valore predefinito che dava la libreria.
         *
         * `vue-data-tables` faceva `type: button.type || 'text'`: senza indicazioni il bottone azione
         * era **senza sfondo**. In element-plus `type="text"` non esiste piu, l'equivalente e la prop
         * booleana `link`. Ometterlo riempirebbe di bottoni pieni tutte le colonne azione - e solo 3
         * punti d'uso su 43 passano un `type` esplicito, quindi si vedrebbe quasi ovunque.
         */
        propsBottone(bottone: { props?: Record<string, unknown> }): Record<string, unknown> {
            const props = bottone.props ?? {};

            // Un tipo esplicito e una scelta del chiamante e si rispetta.
            if ('type' in props) return props;

            // Senza tipo il bottone e un link, come lo era per `vue-data-tables`, e va **blu**: il
            // `type="text"` di Element UI lo era, mentre il `link` di element-plus da solo prende il
            // grigio del testo (`--el-button-text-color`). Con il tipo, la regola
            // `.el-button--primary.is-link` riporta il colore primario. Senza, i link delle liste -
            // "Dettagli" in testa - non si distinguono dal testo intorno: e il rilievo della PO
            // valido "un po' ovunque sulle liste".
            return { link: true, ...props, type: 'primary' };
        },

        componiQuery(tipo: TipoQuery): Record<string, unknown> {
            return {
                type: tipo,
                page: this.paginaCorrente,
                pageSize: this.dimensionePagina,
                sort: this.ordinamento,
                // Per riferimento, non copiati: i chiamanti si aspettano di vedere le proprie mutazioni.
                filters: this.filters,
            };
        },

        emetti(tipo: TipoQuery) {
            this.$emit('query-change', this.componiQuery(tipo));
        },

        alCambioOrdinamento({ prop, order }: Ordinamento) {
            /*
             * Senza `prop` l'ordinamento non e esprimibile, e **non si manda**.
             *
             * Otto colonne del gestionale sono dichiarate `sortable="custom"` senza `prop` - tutte in
             * liste di documenti, per lo piu "Data caricamento". element-plus emette allora
             * `sort-change` con `prop: null`, e inoltrarlo fa rispondere il backend **500**: fa
             * `ToTitleCase()` su `sort.prop` e su null solleva `NullReferenceException`. E la stessa
             * insidia descritta in cima a questo file, che qui si presentava dall'altro verso.
             *
             * Quelle colonne non ordinavano nemmeno prima del porting - il difetto e preesistente,
             * `llm/code-debt/architecture.md` voce 0 - ma con `vue-data-tables` restava un nulla di
             * fatto silenzioso. Tenendo `sort` vuoto si torna a quel comportamento: il click non fa
             * nulla, invece di rompere la lista. Dare un `prop` vero a quelle colonne e un intervento
             * a se, che richiede di sapere su quali campi il backend sa davvero ordinare.
             */
            const nuovo: Ordinamento = prop ? { prop, order } : {};

            // Si emette solo a ordinamento davvero cambiato: senza questa guardia ogni click produce una
            // query in piu, e insieme al watch sui filtri puo innescare un ciclo.
            if (this.ordinamento.prop === nuovo.prop && this.ordinamento.order === nuovo.order) return;
            this.ordinamento = nuovo;
            this.emetti('sort');
        },

        alCambioDimensione(dimensione: number) {
            this.dimensionePagina = dimensione;
            this.emetti('size');
        },

        /** Rilancia la query dall'esterno. Usato da `JobTasksList.ts` quando cambia un filtro che non e nella tabella. */
        queryChange(tipo: TipoQuery = 'filter') {
            this.emetti(tipo);
        },

        /**
         * Inoltra la selezione all'`el-table` interno.
         *
         * Chiude un'anomalia preesistente: `SitesPublishingList.ts` chiamava `toggleRowSelection` sul
         * `ref` del wrapper, che non ce l'aveva - il cast a `Table` nascondeva il problema al
         * compilatore e la selezione non avveniva. Ora la chiamata arriva a destinazione.
         */
        toggleRowSelection(riga: unknown, selezionata?: boolean) {
            (this.$refs.elTable as any)?.toggleRowSelection(riga, selezionata);
        },

        clearSelection() {
            (this.$refs.elTable as any)?.clearSelection();
        },

        setCurrentRow(riga: unknown) {
            (this.$refs.elTable as any)?.setCurrentRow(riga);
        },
    },
});
</script>
